/**
 * Lazy Google Maps loader utility
 * Loads Google Maps API only when needed and prevents duplicate loading
 */

let isLoading = false;
let isLoaded = false;
let loadPromise = null;

/**
 * Lazy load Google Maps API
 * @param {string} apiKey - Google Maps API key
 * @param {string[]} libraries - Libraries to load (e.g., ['places'])
 * @param {{ region?: string }} [options] - e.g. `{ region: 'uk' }` to bias toward the United Kingdom (BCP 47 region)
 * @returns {Promise} Promise that resolves when Google Maps is loaded
 */
export const loadGoogleMaps = (apiKey, libraries = [], options = {}) => {
    // Return existing promise if already loading
    if (isLoading && loadPromise) {
        return loadPromise;
    }

    // Return resolved promise if already loaded
    if (isLoaded && window.google && window.google.maps) {
        return Promise.resolve(window.google);
    }

    // Start loading
    isLoading = true;
    
    loadPromise = new Promise((resolve, reject) => {
        // Check if Google Maps is already available
        if (window.google && window.google.maps) {
            isLoaded = true;
            isLoading = false;
            resolve(window.google);
            return;
        }

        // Create callback function name
        const callbackName = 'initGoogleMaps';
        
        // Create global callback
        window[callbackName] = () => {
            isLoaded = true;
            isLoading = false;
            // Clean up callback
            delete window[callbackName];
            resolve(window.google);
        };

        // Create script element
        const script = document.createElement('script');
        script.type = 'text/javascript';
        script.async = true;
        script.defer = true;
        
        // Build URL with parameters
        const params = new URLSearchParams({
            key: apiKey,
            callback: callbackName,
            loading: 'async'
        });
        
        if (libraries.length > 0) {
            params.append('libraries', libraries.join(','));
        }

        if (options.region) {
            params.append('region', options.region);
        }
        
        script.src = `https://maps.googleapis.com/maps/api/js?${params.toString()}`;
        
        // Handle script loading errors
        script.onerror = () => {
            isLoading = false;
            isLoaded = false;
            delete window[callbackName];
            reject(new Error('Failed to load Google Maps API'));
        };

        // Add script to document
        document.head.appendChild(script);
    });

    return loadPromise;
};

/**
 * Check if Google Maps is already loaded
 * @returns {boolean} True if Google Maps is loaded
 */
export const isGoogleMapsLoaded = () => {
    return isLoaded && window.google && window.google.maps;
};

/**
 * Get Google Maps API key from environment
 * @returns {string} API key
 */
export const getGoogleMapsApiKey = () => {
    return process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY;
};
